import React, { useState, useEffect } from 'react';
import { Mail, Eye, Clock, CheckCircle, XCircle } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { getEnquiries, updateEnquiryStatus } from '../../services/api';
import { toast } from 'sonner';

const EnquiriesAdmin = () => {
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');

  const fetchData = async () => {
    try {
      const res = await getEnquiries({ status: filter || undefined });
      setEnquiries(res.data.enquiries);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, [filter]);

  const handleStatusChange = async (id, newStatus) => {
    try {
      await updateEnquiryStatus(id, newStatus);
      toast.success('Status updated');
      fetchData();
    } catch { toast.error('Update failed'); }
  };

  const statusColor = (s) => {
    switch(s) {
      case 'new': return 'bg-blue-50 text-blue-700';
      case 'contacted': return 'bg-yellow-50 text-yellow-700';
      case 'closed': return 'bg-green-50 text-green-700';
      default: return 'bg-gray-50 text-gray-700';
    }
  };

  return (
    <div className="space-y-6" data-testid="enquiries-admin">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Enquiries</h1>
          <p className="text-sm text-gray-500">{enquiries.length} enquiries</p>
        </div>
      </div>

      <div className="flex gap-2">
        {['', 'new', 'contacted', 'closed'].map((s) => (
          <Button key={s} size="sm" variant={filter === s ? 'default' : 'outline'} onClick={() => setFilter(s)} className={filter === s ? 'bg-[#2E3A6F]' : ''}>
            {s || 'All'}
          </Button>
        ))}
      </div>

      {loading ? (
        <div className="space-y-3">{[...Array(4)].map((_, i) => <Card key={i} className="animate-pulse"><CardContent className="p-4 h-20" /></Card>)}</div>
      ) : enquiries.length === 0 ? (
        <Card className="border-dashed"><CardContent className="p-12 text-center text-gray-500">No enquiries found</CardContent></Card>
      ) : (
        <div className="space-y-3">
          {enquiries.map((enq) => (
            <Card key={enq.id} className="hover:shadow-sm transition-shadow" data-testid={`enquiry-${enq.id}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-gray-900">{enq.name}</h3>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium capitalize ${statusColor(enq.status)}`}>{enq.status}</span>
                    </div>
                    <div className="text-xs text-gray-500 space-y-0.5">
                      <p><Mail size={12} className="inline mr-1" />{enq.email} &middot; {enq.phone}</p>
                      {enq.company && <p>Company: {enq.company}</p>}
                      {enq.subject && <p>Subject: {enq.subject}</p>}
                      {enq.product_name && <p>Product: {enq.product_name}</p>}
                    </div>
                    <p className="text-sm text-gray-700 mt-2 bg-gray-50 p-3 rounded-lg">{enq.message}</p>
                    <p className="text-[10px] text-gray-400 mt-2">{enq.created_at && new Date(enq.created_at).toLocaleString()}</p>
                  </div>
                  <div className="flex gap-1 ml-3">
                    {enq.status !== 'contacted' && (
                      <Button size="sm" variant="outline" onClick={() => handleStatusChange(enq.id, 'contacted')} className="text-yellow-600 text-xs">
                        <Clock size={12} className="mr-1" />Contacted
                      </Button>
                    )}
                    {enq.status !== 'closed' && (
                      <Button size="sm" variant="outline" onClick={() => handleStatusChange(enq.id, 'closed')} className="text-green-600 text-xs">
                        <CheckCircle size={12} className="mr-1" />Close
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default EnquiriesAdmin;
